'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  Toaster as ChakraToaster,
  createToaster,
  type CreateToasterReturn,
  type ToastOptions,
  Box,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { LuCircleCheck, LuCircleX, LuTriangleAlert, LuInfo, LuX } from 'react-icons/lu';

// Re-export createToaster for external usage
export { createToaster };

// Create toaster instance with proper typing
export const toaster: CreateToasterReturn = createToaster({
  placement: 'top',
  pauseOnPageIdle: true,
  overlap: true,
  gap: 12,
});

// ── Status Config ───────────────────────────────────────────────────

const statusConfig = {
  success: {
    icon: LuCircleCheck,
    accentGradient: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)',
    progressColor: '#22c55e',
    iconColor: 'green.500',
    bgLight: 'rgba(240, 253, 244, 0.92)',
    bgDark: 'rgba(6, 78, 59, 0.85)',
    borderLight: 'rgba(34, 197, 94, 0.25)',
    borderDark: 'rgba(34, 197, 94, 0.35)',
    titleColorLight: '#14532d',
    titleColorDark: '#86efac',
    descColorLight: '#166534',
    descColorDark: '#bbf7d0',
  },
  error: {
    icon: LuCircleX,
    accentGradient: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)',
    progressColor: '#ef4444',
    iconColor: 'red.500',
    bgLight: 'rgba(254, 242, 242, 0.92)',
    bgDark: 'rgba(127, 29, 29, 0.85)',
    borderLight: 'rgba(239, 68, 68, 0.25)',
    borderDark: 'rgba(239, 68, 68, 0.35)',
    titleColorLight: '#7f1d1d',
    titleColorDark: '#fca5a5',
    descColorLight: '#991b1b',
    descColorDark: '#fecaca',
  },
  warning: {
    icon: LuTriangleAlert,
    accentGradient: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)',
    progressColor: '#f59e0b',
    iconColor: 'orange.500',
    bgLight: 'rgba(255, 251, 235, 0.92)',
    bgDark: 'rgba(124, 45, 18, 0.85)',
    borderLight: 'rgba(245, 158, 11, 0.25)',
    borderDark: 'rgba(245, 158, 11, 0.35)',
    titleColorLight: '#78350f',
    titleColorDark: '#fcd34d',
    descColorLight: '#92400e',
    descColorDark: '#fde68a',
  },
  info: {
    icon: LuInfo,
    accentGradient: 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)',
    progressColor: '#3b82f6',
    iconColor: 'blue.500',
    bgLight: 'rgba(239, 246, 255, 0.92)',
    bgDark: 'rgba(30, 58, 138, 0.85)',
    borderLight: 'rgba(59, 130, 246, 0.25)',
    borderDark: 'rgba(59, 130, 246, 0.35)',
    titleColorLight: '#1e3a8a',
    titleColorDark: '#93c5fd',
    descColorLight: '#1e40af',
    descColorDark: '#bfdbfe',
  },
} as const;

type ToastType = keyof typeof statusConfig;

// ── Progress Bar ────────────────────────────────────────────────────

function ToastProgressBar({
  duration,
  color,
  paused,
}: {
  duration: number;
  color: string;
  paused: boolean;
}) {
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!progressRef.current || duration <= 0 || duration === Infinity) return;
    const el = progressRef.current;
    el.style.animation = `axon-progress-shrink ${duration}ms linear forwards`;
    el.style.animationPlayState = paused ? 'paused' : 'running';
    return () => {
      el.style.animation = '';
    };
  }, [duration, paused]);

  if (duration <= 0 || duration === Infinity) return null;

  return (
    <Box
      position="absolute"
      bottom="0"
      left="0"
      right="0"
      height="3px"
      background="rgba(0,0,0,0.06)"
      overflow="hidden"
      borderBottomRadius="lg"
    >
      <Box ref={progressRef} height="full" width="full" background={color} opacity={0.8} />
    </Box>
  );
}

// ── Modern Toast Item ───────────────────────────────────────────────

function ModernToastItem({
  toast,
}: {
  toast: ToastOptions & {
    action?: { label: string; onClick: () => void };
  };
}) {
  const type = (toast.type as ToastType | undefined) ?? 'info';
  const config = statusConfig[type];
  const Icon = config.icon;
  const [isPaused, setIsPaused] = useState(false);

  return (
    <Box
      position="relative"
      width="full"
      maxW="420px"
      borderRadius="lg"
      overflow="hidden"
      borderWidth="1px"
      borderStyle="solid"
      borderLeftWidth="4px"
      borderLeftColor={config.progressColor}
      background={config.bgLight}
      backdropFilter="blur(12px) saturate(180%)"
      style={{ WebkitBackdropFilter: 'blur(12px) saturate(180%)' }}
      boxShadow="0 8px 32px -8px rgba(0, 0, 0, 0.12), 0 2px 8px -2px rgba(0, 0, 0, 0.06)"
      transition="all 0.2s cubic-bezier(0.16, 1, 0.3, 1)"
      _hover={{
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 40px -8px rgba(0, 0, 0, 0.16), 0 4px 12px -2px rgba(0, 0, 0, 0.08)',
      }}
      _dark={{
        background: config.bgDark,
        borderColor: config.borderDark,
        boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.35), 0 2px 8px -2px rgba(0, 0, 0, 0.2)',
      }}
      borderColor={config.borderLight}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      className="animate-axon-slide-in"
    >
      <HStack gap={3} align="flex-start" padding={4} paddingBottom={toast.duration ? 5 : 4}>
        {/* Icon */}
        <Box
          flexShrink={0}
          marginTop="2px"
          color={config.iconColor}
          fontSize="xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon size={22} strokeWidth={2.2} />
        </Box>

        {/* Content */}
        <Box flex="1" minW={0}>
          {toast.title && (
            <Box
              as="span"
              display="block"
              fontWeight="semibold"
              fontSize="sm"
              lineHeight="short"
              color={config.titleColorLight}
              _dark={{ color: config.titleColorDark }}
              marginBottom={toast.description ? '2px' : '0'}
            >
              {toast.title}
            </Box>
          )}
          {toast.description && (
            <Box
              as="span"
              display="block"
              fontSize="sm"
              lineHeight="short"
              color={config.descColorLight}
              _dark={{ color: config.descColorDark }}
            >
              {toast.description}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <HStack gap={1} flexShrink={0} align="center">
          {toast.action && (
            <IconButton
              aria-label={toast.action.label}
              size="xs"
              variant="ghost"
              onClick={toast.action.onClick}
              color={config.titleColorLight}
              _dark={{ color: config.titleColorDark }}
              fontSize="xs"
              fontWeight="medium"
              height="auto"
              paddingX={2}
              paddingY={1}
              borderRadius="md"
              _hover={{
                background: 'rgba(0,0,0,0.06)',
              }}
            >
              {toast.action.label}
            </IconButton>
          )}
          {toast.closable !== false && (
            <IconButton
              aria-label="Close"
              size="xs"
              variant="ghost"
              onClick={() => toaster.dismiss(toast.id ?? '')}
              color={config.descColorLight}
              borderRadius="full"
              _hover={{
                background: 'rgba(0,0,0,0.06)',
                color: config.titleColorLight,
              }}
            >
              <LuX size={14} />
            </IconButton>
          )}
        </HStack>
      </HStack>

      {/* Progress bar */}
      <ToastProgressBar
        duration={toast.duration ?? 5000}
        color={config.progressColor}
        paused={isPaused}
      />
    </Box>
  );
}

// ── Toaster Export ──────────────────────────────────────────────────

export const Toaster: React.FC = () => {
  return (
    <ChakraToaster toaster={toaster}>{(toast) => <ModernToastItem toast={toast} />}</ChakraToaster>
  );
};
